// src/components/routing/TechnicianSkillsManager.js
// FlowSense AI — Enhanced Technician Skills Manager v2.0
// Glass Morphism, Framer Motion, Skill Coverage Chart, Performance Optimized
// --- UPDATE (FlowSense v3 · AI Explainer)
//   • All field roles: technicians, inspectors and contractors are listed (was tech + staff only),
//     with role badges; legacy role spellings (Technician, field_tech) are recognised.
//   • Bug fix: <select> values are strings, so numeric user ids never matched and the editor
//     stayed empty — ids are now compared as strings.
//   • FlowSense AI explainer: skill-coverage findings from the real skill data — skills only one
//     person has (single point of failure), common skills nobody has, people with no skills (the
//     AI Dispatcher cannot match them by skill) — plus an AI explanation.
//   • "Copy skills from…" another person, and a coverage list for every skill (not just the top 5).
// --- END UPDATE

import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import {
    Box, Heading, FormControl, FormLabel, Select, Textarea, Button, Spinner, Alert,
    AlertIcon, useToast, VStack, Text, HStack, Tag, TagLabel, TagCloseButton, Wrap,
    WrapItem, Input, IconButton, Tooltip, Card, CardBody, Divider, Badge, SimpleGrid,
    Stat, StatLabel, StatNumber, StatHelpText, InputGroup, InputLeftElement, InputRightElement,
    useColorModeValue, Flex, Menu, MenuButton, MenuList, MenuItem, Icon, Skeleton,
    Progress, Collapse,
} from '@chakra-ui/react';
import {
    AddIcon, DeleteIcon, SearchIcon, CheckIcon, InfoIcon, EditIcon, CloseIcon,
} from '@chakra-ui/icons';
import {
    MdEngineering, MdBuild, MdStar, MdTrendingUp, MdRefresh,
    MdCheckCircle, MdWarning, MdLightbulb, MdCategory,
} from 'react-icons/md';
import { motion, AnimatePresence } from 'framer-motion';
import * as userService from '../../services/userService';
// --- UPDATE (AI Explainer)
import FlowSenseAIExplainer from '../ai/FlowSenseAIExplainer';
const ROLE_ALIASES = { technician: 'tech', technicians: 'tech', field_tech: 'tech', field_technician: 'tech', fco: 'tech', inspectors: 'inspector', field_inspector: 'inspector', contractors: 'contractor', vendor: 'contractor', subcontractor: 'contractor' };
const normRole = (r) => { const k = String(r || '').trim().toLowerCase().replace(/[\s-]+/g, '_'); return ROLE_ALIASES[k] || k; };
const SKILL_ROLES = ['tech', 'inspector', 'contractor', 'staff'];
const ROLE_SCHEME = { tech: 'green', inspector: 'orange', contractor: 'yellow', staff: 'cyan' };
const isSkillUser = (u) => SKILL_ROLES.includes(normRole(u.role)) && (!u.status || u.status === 'active');
// --- END UPDATE

// ─────────────────────────────────────────────────────────────────────────────
// MOTION WRAPPERS & PRESETS
// ─────────────────────────────────────────────────────────────────────────────
const MotionBox  = motion(Box);
const MotionFlex = motion(Flex);

const ANIM = {
    fadeInUp:   { initial:{ opacity:0, y:16 },  animate:{ opacity:1, y:0 } },
    fadeInDown: { initial:{ opacity:0, y:-16 }, animate:{ opacity:1, y:0 } },
    scaleIn:    { initial:{ opacity:0, scale:0.92 }, animate:{ opacity:1, scale:1 } },
    stagger:    (i, base=0.05) => ({ delay: i * base }),
    spring:     { type:'spring', stiffness:300, damping:24 },
    smooth:     { duration:0.3, ease:[0.25, 0.46, 0.45, 0.94] },
};

const PulseIndicator = ({ color='green', size=2 }) => (
    <Box position="relative" display="inline-flex" w={`${size*4}px`} h={`${size*4}px`} flexShrink={0}>
        <MotionBox position="absolute" inset={0} borderRadius="full" bg={`${color}.400`}
            animate={{ scale:[1,1.8,1], opacity:[0.7,0,0.7] }}
            transition={{ duration:2, repeat:Infinity, ease:'easeInOut' }} />
        <Box position="absolute" inset={0} borderRadius="full" bg={`${color}.500`} />
    </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS (original)
// ─────────────────────────────────────────────────────────────────────────────
const COMMON_SKILLS = [
    'pump_mechanics', 'electrical', 'valve_repair', 'pipe_welding', 'plumbing',
    'hvac', 'diagnostics', 'meter_installation', 'leak_detection', 'pressure_testing',
    'emergency_response', 'preventive_maintenance',
];

const SKILL_COLORS = {
    pump_mechanics:'blue', electrical:'yellow', valve_repair:'purple', pipe_welding:'orange',
    plumbing:'cyan', hvac:'teal', diagnostics:'pink', meter_installation:'green',
    leak_detection:'red', pressure_testing:'blue', emergency_response:'red', preventive_maintenance:'green',
};
const getSkillColor = (skill) => SKILL_COLORS[skill] || 'gray';

// ─────────────────────────────────────────────────────────────────────────────
// SHARED SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
const EnhancedStatCard = memo(({ label, value, helpText, icon, color='blue', isLoading:loading, children }) => {
    const cardBg      = useColorModeValue('white','gray.800');
    const borderColor = useColorModeValue('gray.200','gray.600');
    const mutedColor  = useColorModeValue('gray.500','gray.400');
    return (
        <MotionBox whileHover={{ y:-3, boxShadow:'0 8px 25px rgba(0,0,0,0.1)' }} transition={{ duration:0.2 }}>
            <Card bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="xl"
                overflow="hidden" position="relative">
                <Box position="absolute" top={0} left={0} right={0} h="3px"
                    bgGradient={`linear(to-r, ${color}.400, ${color}.600)`} />
                <CardBody py={4} px={5}>
                    {loading ? (
                        <VStack align="start" spacing={2}>
                            <Skeleton h="12px" w="80px" borderRadius="md" />
                            <Skeleton h="28px" w="50px" borderRadius="md" />
                        </VStack>
                    ) : (
                        <HStack justify="space-between" align="start">
                            <Stat size="sm">
                                <StatLabel fontSize="xs" fontWeight="600" color={mutedColor}
                                    textTransform="uppercase" letterSpacing="wider">{label}</StatLabel>
                                <StatNumber fontSize="2xl" fontWeight="800" color={`${color}.500`}>
                                    {children || value}
                                </StatNumber>
                                <StatHelpText fontSize="xs" mb={0}>{helpText}</StatHelpText>
                            </Stat>
                            {icon && (
                                <Box p={2} borderRadius="xl" bg={`${color}.50`}>
                                    <Icon as={icon} boxSize={5} color={`${color}.500`} />
                                </Box>
                            )}
                        </HStack>
                    )}
                </CardBody>
            </Card>
        </MotionBox>
    );
});

const EmptyState = memo(({ icon:IconComp, title, subtitle }) => (
    <Flex direction="column" align="center" justify="center" py={10} gap={3}>
        <MotionBox animate={{ y:[0,-6,0] }} transition={{ duration:3, repeat:Infinity, ease:'easeInOut' }}>
            <Icon as={IconComp || MdEngineering} boxSize={14} color="gray.300" />
        </MotionBox>
        <Text fontWeight="700" color="gray.400">{title}</Text>
        {subtitle && <Text fontSize="sm" color="gray.400" textAlign="center" maxW="300px">{subtitle}</Text>}
    </Flex>
));

/** Animated skill tag with remove */
const SkillTag = memo(({ skill, onRemove, index }) => (
    <MotionBox initial={{ opacity:0, scale:0.8 }} animate={{ opacity:1, scale:1 }}
        exit={{ opacity:0, scale:0.5 }}
        transition={{ delay: index * 0.02, duration:0.2 }}>
        <Tag size="lg" colorScheme={getSkillColor(skill)} borderRadius="full"
            _hover={{ transform:'translateY(-1px)', boxShadow:'sm' }} transition="all 0.15s"
            cursor="default">
            <TagLabel fontSize="sm">{skill.replace(/_/g, ' ')}</TagLabel>
            <TagCloseButton onClick={() => onRemove(skill)} />
        </Tag>
    </MotionBox>
));

/** Quick-add skill button */
const QuickSkillButton = memo(({ skill, onAdd }) => (
    <MotionBox whileHover={{ scale:1.04 }} whileTap={{ scale:0.96 }}>
        <Button size="sm" variant="outline" leftIcon={<AddIcon boxSize={2.5} />}
            onClick={() => onAdd(skill)} borderRadius="full" fontWeight="600" fontSize="xs"
            _hover={{ borderColor:'blue.400', bg:'blue.50' }} transition="all 0.15s">
            {skill.replace(/_/g, ' ')}
        </Button>
    </MotionBox>
));

/** Top skill bar — mini horizontal bar chart */
const TopSkillsBar = memo(({ topSkills, totalTechnicians }) => {
    const barBg    = useColorModeValue('gray.100','gray.700');
    const mutedClr = useColorModeValue('gray.500','gray.400');
    if (!topSkills || topSkills.length === 0) return null;
    return (
        <VStack align="stretch" spacing={2}>
            <Text fontSize="2xs" fontWeight="700" color={mutedClr} textTransform="uppercase"
                letterSpacing="wider">Top Skills Coverage</Text>
            {topSkills.slice(0, 5).map(([skill, count], i) => {
                const pct = totalTechnicians > 0 ? Math.round((count / totalTechnicians) * 100) : 0;
                return (
                    <HStack key={skill} spacing={2}>
                        <Text fontSize="2xs" fontWeight="600" w="100px" noOfLines={1} color={mutedClr}>
                            {skill.replace(/_/g, ' ')}
                        </Text>
                        <Box flex={1} bg={barBg} borderRadius="full" h="6px" overflow="hidden">
                            <MotionBox initial={{ width:0 }}
                                animate={{ width:`${pct}%` }}
                                transition={{ delay:0.3 + i * 0.1, duration:0.5 }}
                                h="full" borderRadius="full"
                                bgGradient={`linear(to-r, ${getSkillColor(skill)}.400, ${getSkillColor(skill)}.600)`} />
                        </Box>
                        <Text fontSize="2xs" fontWeight="700" color={mutedClr} w="32px" textAlign="right">
                            {pct}%
                        </Text>
                    </HStack>
                );
            })}
        </VStack>
    );
});

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
function TechnicianSkillsManager() {
    // ── State (all original) ─────────────────────────────────────────────────
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [skillsText, setSkillsText] = useState('');
    const [newSkillInput, setNewSkillInput] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [isDirty, setIsDirty] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterBySkills, setFilterBySkills] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const toast = useToast();

    // ── Theme tokens ─────────────────────────────────────────────────────────
    const bgColor        = useColorModeValue('white', 'gray.800');
    const borderColor    = useColorModeValue('gray.200', 'gray.600');
    const mutedColor     = useColorModeValue('gray.500', 'gray.400');
    const searchBg       = useColorModeValue('white', 'gray.700');
    const headerGradient = useColorModeValue(
        'linear(to-r, teal.500, green.500)',
        'linear(to-r, teal.400, green.400)'
    );

    // ── Data Fetching (original logic preserved) ─────────────────────────────
    const fetchUsersData = useCallback(async () => {
        try {
            setIsLoading(true); setError(null);
            const response = await userService.fetchUsers();
            const filteredUsers = Array.isArray(response.data)
                ? response.data.filter(isSkillUser) // --- UPDATE: + inspector, contractor; role aliases
                : [];
            setUsers(filteredUsers);
        } catch (err) {
            const errorMessage = err?.response?.data?.message || err?.message || 'Failed to fetch users.';
            setError(errorMessage);
            toast({ title:'Error fetching users', description:errorMessage, status:'error', duration:5000, isClosable:true });
        } finally { setIsLoading(false); }
    }, [toast]);

    useEffect(() => { fetchUsersData(); }, [fetchUsersData]);

    // ── Derived data (original logic preserved) ──────────────────────────────
    const selectedUser = useMemo(() => users.find(u => String(u.id) === String(selectedUserId)), [users, selectedUserId]); // --- UPDATE: string-safe

    const parseSkills = useCallback((rawSkills) => {
        if (!rawSkills) return [];
        if (Array.isArray(rawSkills)) return rawSkills;
        if (typeof rawSkills === 'string') {
            if (rawSkills.trim() === '' || rawSkills.trim() === '[]') return [];
            try { const parsed = JSON.parse(rawSkills); return Array.isArray(parsed) ? parsed : []; }
            catch (e) { console.error("Failed to parse skills JSON:", rawSkills, e); return []; }
        }
        return [];
    }, []);

    useEffect(() => {
        if (selectedUser) { setSkillsText(parseSkills(selectedUser.skills).join('\n')); setIsDirty(false); }
        else { setSkillsText(''); setIsDirty(false); }
    }, [selectedUser, parseSkills]);

    const currentSkills = useMemo(() =>
        skillsText.split('\n').map(s => s.trim()).filter(s => s.length > 0),
    [skillsText]);

    const filteredUsers = useMemo(() =>
        users.filter(user => {
            const matchesSearch = !searchTerm ||
                user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.email?.toLowerCase().includes(searchTerm.toLowerCase());
            const hasSkills = parseSkills(user.skills).length > 0;
            return matchesSearch && (!filterBySkills || hasSkills);
        }),
    [users, searchTerm, filterBySkills, parseSkills]);

    const skillStats = useMemo(() => {
        const allSkills = users.reduce((acc, user) => [...acc, ...parseSkills(user.skills)], []);
        const uniqueSkills = [...new Set(allSkills)];
        const skillCounts = {};
        allSkills.forEach(skill => { skillCounts[skill] = (skillCounts[skill] || 0) + 1; });
        const topSkills = Object.entries(skillCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);
        return {
            totalTechnicians: users.length,
            withSkills: users.filter(u => parseSkills(u.skills).length > 0).length,
            totalUniqueSkills: uniqueSkills.length,
            topSkills,
            allSkills: Object.entries(skillCounts).sort((a, b) => b[1] - a[1]), // --- UPDATE
        };
    }, [users, parseSkills]);

    // --- UPDATE: explainer findings from the real skill data
    const explainer = useMemo(() => {
        const findings = [];
        const counts = Object.fromEntries(skillStats.allSkills);
        const holders = (skill) => users.filter(u => parseSkills(u.skills).includes(skill)).map(u => u.username);
        const single = skillStats.allSkills.filter(([, n]) => n === 1).map(([s]) => s);
        const missingCommon = COMMON_SKILLS.filter(s => !counts[s]);
        const noSkills = users.filter(u => parseSkills(u.skills).length === 0);
        if (missingCommon.includes('emergency_response')) findings.push({ severity: 'high', text: 'Nobody has the emergency_response skill, so the AI Dispatcher cannot pick a qualified crew for emergencies.' });
        if (missingCommon.filter(s => s !== 'emergency_response').length) findings.push({ severity: 'medium', text: `${missingCommon.filter(s => s !== 'emergency_response').length} common skill(s) are held by nobody.`, evidence: missingCommon.filter(s => s !== 'emergency_response').map(s => s.replace(/_/g, ' ')).join(', ') });
        if (single.length) findings.push({ severity: 'medium', text: `${single.length} skill(s) depend on a single person — if they are off, nobody can take that work.`, evidence: single.slice(0, 6).map(s => `${s.replace(/_/g, ' ')} (${holders(s)[0]})`).join(', ') });
        if (noSkills.length) findings.push({ severity: noSkills.length > users.length / 2 ? 'high' : 'medium', text: `${noSkills.length} of ${users.length} people have no skills recorded, so skill-based dispatch skips them.`, evidence: noSkills.slice(0, 6).map(u => u.username).join(', ') });
        const byRole = {};
        users.forEach(u => { const r = normRole(u.role); byRole[r] = (byRole[r] || 0) + 1; });
        if (users.length && !findings.length) findings.push({ severity: 'ok', text: 'Every common skill is covered by at least two people.' });
        return {
            findings,
            facts: {
                people: users.length, by_role: byRole, with_skills: skillStats.withSkills,
                skill_coverage: skillStats.allSkills.slice(0, 30).map(([skill, n]) => ({ skill, people: n })),
                single_holder_skills: single.slice(0, 20), common_skills_missing: missingCommon,
                selected_person: selectedUser ? { name: selectedUser.username, role: normRole(selectedUser.role), skills: currentSkills } : null,
            },
        };
    }, [skillStats, users, parseSkills, selectedUser, currentSkills]);

    const copySources = useMemo(() => users.filter(u => String(u.id) !== String(selectedUserId) && parseSkills(u.skills).length > 0), [users, selectedUserId, parseSkills]);
    // --- END UPDATE

    // ── Handlers (all original logic preserved) ──────────────────────────────
    const handleSave = useCallback(async () => {
        if (!selectedUserId) { toast({ title:'No user selected', status:'warning', duration:3000, isClosable:true }); return; }
        setIsSaving(true);
        try {
            await userService.updateUserSkills(selectedUserId, currentSkills);
            toast({ title:'Skills Updated', description:`Skills for ${selectedUser.username} saved.`, status:'success', duration:3000, isClosable:true });
            const response = await userService.fetchUsers();
            setUsers(Array.isArray(response.data) ? response.data.filter(isSkillUser) : []); // --- UPDATE
            setIsDirty(false);
        } catch (err) {
            toast({ title:'Save Failed', description:err?.response?.data?.message || err?.message || 'Failed to save skills', status:'error', duration:5000, isClosable:true });
        } finally { setIsSaving(false); }
    }, [selectedUserId, currentSkills, selectedUser, toast]);

    const handleAddSkill = useCallback((skill) => {
        const trimmed = skill.trim().toLowerCase();
        if (!trimmed) return;
        if (currentSkills.includes(trimmed)) { toast({ title:'Skill already exists', status:'info', duration:2000, isClosable:true }); return; }
        setSkillsText([...currentSkills, trimmed].join('\n'));
        setNewSkillInput('');
        setIsDirty(true);
    }, [currentSkills, toast]);

    const handleRemoveSkill = useCallback((skillToRemove) => {
        setSkillsText(currentSkills.filter(s => s !== skillToRemove).join('\n'));
        setIsDirty(true);
    }, [currentSkills]);

    const handleTextareaChange = useCallback((e) => { setSkillsText(e.target.value); setIsDirty(true); }, []);

    const handleUserChange = useCallback((e) => {
        if (isDirty) {
            if (window.confirm('You have unsaved changes. Switch users?')) setSelectedUserId(e.target.value);
        } else setSelectedUserId(e.target.value);
    }, [isDirty]);

    // --- UPDATE: merge another person's skills into the editor
    const handleCopyFrom = useCallback((e) => {
        const src = users.find(u => String(u.id) === String(e.target.value));
        e.target.value = '';
        if (!src) return;
        const merged = [...new Set([...currentSkills, ...parseSkills(src.skills)])];
        const added = merged.length - currentSkills.length;
        setSkillsText(merged.join('\n'));
        if (added) setIsDirty(true);
        toast({ title: added ? `${added} skill(s) copied from ${src.username}` : `No new skills from ${src.username}`, status: added ? 'success' : 'info', duration: 2500, isClosable: true });
    }, [users, currentSkills, parseSkills, toast]);
    // --- END UPDATE

    const handleResetSkills = useCallback(() => {
        if (window.confirm('Clear all skills for this technician?')) { setSkillsText(''); setIsDirty(true); }
    }, []);

    // ── Loading / Error States ───────────────────────────────────────────────
    if (isLoading && users.length === 0) {
        return (
            <Flex direction="column" align="center" justify="center" py={16} gap={4}>
                <Spinner size="xl" color="teal.500" thickness="4px" />
                <Text color={mutedColor} fontSize="sm">Loading technicians…</Text>
            </Flex>
        );
    }

    if (error && users.length === 0) {
        return <Alert status="error" borderRadius="lg"><AlertIcon />{error}</Alert>;
    }

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <Box>
            {/* ═══════ HEADER ═══════ */}
            <MotionFlex {...ANIM.fadeInDown} transition={ANIM.smooth}
                justifyContent="space-between" alignItems="center" mb={6}>
                <HStack spacing={4}>
                    <MotionBox initial={{ scale:0, rotate:-180 }} animate={{ scale:1, rotate:0 }}
                        transition={{ type:'spring', stiffness:200, damping:15 }}>
                        <Box bgGradient={headerGradient} p={3} borderRadius="xl"
                            boxShadow="0 4px 14px rgba(56,178,172,0.3)">
                            <Icon as={MdEngineering} color="white" boxSize={6} />
                        </Box>
                    </MotionBox>
                    <Box>
                        <Heading size="md" fontWeight="800">Technician Skill Management</Heading>
                        <HStack spacing={2} mt={0.5}>
                            <Text color={mutedColor} fontSize="sm">Manage and track technician skills for better assignment</Text>
                            <PulseIndicator color="teal" size={1.5} />
                        </HStack>
                    </Box>
                </HStack>
                <Tooltip label="Refresh" hasArrow>
                    <IconButton icon={<Icon as={MdRefresh} />} variant="ghost" borderRadius="full"
                        onClick={fetchUsersData} isLoading={isLoading} aria-label="Refresh" />
                </Tooltip>
            </MotionFlex>

            {/* ═══════ STAT CARDS ═══════ */}
            <SimpleGrid columns={{ base:1, md:4 }} spacing={4} mb={6}>
                <MotionBox {...ANIM.fadeInUp} transition={{ ...ANIM.stagger(0), ...ANIM.smooth }}>
                    <EnhancedStatCard label="Total Technicians" value={skillStats.totalTechnicians}
                        helpText="Active in system" icon={MdEngineering} color="blue" />
                </MotionBox>
                <MotionBox {...ANIM.fadeInUp} transition={{ ...ANIM.stagger(1), ...ANIM.smooth }}>
                    <EnhancedStatCard label="With Skills" value={skillStats.withSkills}
                        helpText={`${skillStats.totalTechnicians > 0 ? Math.round((skillStats.withSkills / skillStats.totalTechnicians) * 100) : 0}% coverage`}
                        icon={MdCheckCircle} color="green" />
                </MotionBox>
                <MotionBox {...ANIM.fadeInUp} transition={{ ...ANIM.stagger(2), ...ANIM.smooth }}>
                    <EnhancedStatCard label="Unique Skills" value={skillStats.totalUniqueSkills}
                        helpText="Across all technicians" icon={MdCategory} color="purple" />
                </MotionBox>
                <MotionBox {...ANIM.fadeInUp} transition={{ ...ANIM.stagger(3), ...ANIM.smooth }}>
                    <EnhancedStatCard label="Top Skill" icon={MdStar} color="orange"
                        helpText={`${skillStats.topSkills[0]?.[1] || 0} technicians`}>
                        <Text fontSize="md" fontWeight="800" color="orange.500">
                            {skillStats.topSkills[0]?.[0]?.replace(/_/g,' ') || 'N/A'}
                        </Text>
                    </EnhancedStatCard>
                </MotionBox>
            </SimpleGrid>

            {/* ═══════ TOP SKILLS COVERAGE ═══════ */}
            {skillStats.topSkills.length > 0 && (
                <MotionBox {...ANIM.fadeInUp} transition={{ delay:0.25, ...ANIM.smooth }} mb={6}>
                    <Card bg={bgColor} borderColor={borderColor} borderWidth="1px" borderRadius="xl">
                        <CardBody py={4}>
                            <TopSkillsBar topSkills={skillStats.topSkills} totalTechnicians={skillStats.totalTechnicians} />
                        </CardBody>
                    </Card>
                </MotionBox>
            )}

            {/* ═══════ UPDATE: AI EXPLAINER ═══════ */}
            <MotionBox {...ANIM.fadeInUp} transition={{ delay:0.28, ...ANIM.smooth }} mb={6}>
                <FlowSenseAIExplainer
                    module="technician_skills"
                    title="Can we staff every kind of job?"
                    subtitle="Skill coverage, single points of failure and gaps — plus an AI explanation"
                    facts={explainer.facts}
                    findings={explainer.findings}
                    defaultQuestion="Explain the skill gaps in this workforce and which skills to train or hire for first."
                    questions={['Which skills should we train first?', 'Who is a single point of failure?', 'How does this affect AI dispatch?']}
                />
            </MotionBox>

            {/* ═══════ SEARCH + SELECT ═══════ */}
            <MotionBox {...ANIM.fadeInUp} transition={{ delay:0.3, ...ANIM.smooth }}>
                <Card mb={6} bg={bgColor} borderColor={borderColor} borderWidth="1px" borderRadius="xl">
                    <CardBody py={4}>
                        <VStack spacing={4} align="stretch">
                            <HStack spacing={3} wrap="wrap">
                                <InputGroup maxW="300px" size="sm">
                                    <InputLeftElement pointerEvents="none"><SearchIcon color="gray.400" boxSize={3} /></InputLeftElement>
                                    <Input placeholder="Search technicians…" value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)} bg={searchBg}
                                        borderRadius="lg" fontSize="sm"
                                        _focus={{ borderColor:'teal.400', boxShadow:'0 0 0 1px var(--chakra-colors-teal-400)' }} />
                                    {searchTerm && (
                                        <InputRightElement cursor="pointer" onClick={() => setSearchTerm('')}>
                                            <CloseIcon color="gray.400" boxSize={2} />
                                        </InputRightElement>
                                    )}
                                </InputGroup>
                                <Menu>
                                    <MenuButton as={Button} variant="outline" size="sm" borderRadius="lg">
                                        {filterBySkills ? 'With Skills Only' : 'All Technicians'}
                                    </MenuButton>
                                    <MenuList borderRadius="xl" boxShadow="lg">
                                        <MenuItem onClick={() => setFilterBySkills(false)} borderRadius="md">All Technicians</MenuItem>
                                        <MenuItem onClick={() => setFilterBySkills(true)} borderRadius="md">With Skills Only</MenuItem>
                                    </MenuList>
                                </Menu>
                                {(searchTerm || filterBySkills) && (
                                    <Badge colorScheme="teal" borderRadius="full" px={3} py={1} fontSize="xs">
                                        {filteredUsers.length} of {users.length}
                                    </Badge>
                                )}
                            </HStack>
                            <FormControl>
                                <FormLabel fontSize="sm" fontWeight="600">Select Technician</FormLabel>
                                <Select placeholder="Select a technician…" value={selectedUserId}
                                    onChange={handleUserChange} size="lg" borderRadius="lg"
                                    _focus={{ borderColor:'teal.400', boxShadow:'0 0 0 1px var(--chakra-colors-teal-400)' }}>
                                    {filteredUsers.map(user => {
                                        const uSkills = parseSkills(user.skills);
                                        return (
                                            <option key={user.id} value={user.id}>
                                                {user.username} · {normRole(user.role)} ({user.email}) — {uSkills.length} skills
                                            </option>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </VStack>
                    </CardBody>
                </Card>
            </MotionBox>

            {/* ═══════ SKILL EDITOR ═══════ */}
            <AnimatePresence mode="wait">
                {selectedUserId && selectedUser ? (
                    <MotionBox key="editor" {...ANIM.fadeInUp} transition={ANIM.smooth}>
                        <Card bg={bgColor} borderColor={borderColor} borderWidth="1px" borderRadius="xl"
                            overflow="hidden">
                            <Box position="absolute" top={0} left={0} right={0} h="3px"
                                bgGradient={headerGradient} />
                            <CardBody pt={5}>
                                <VStack spacing={6} align="stretch">
                                    {/* User info header */}
                                    <HStack justify="space-between">
                                        <Box>
                                            <HStack spacing={2}>
                                                <Icon as={MdEngineering} color="teal.500" boxSize={4} />
                                                <Heading size="sm" fontWeight="800">{selectedUser.username}</Heading>
                                                {/* --- UPDATE */}
                                                <Badge colorScheme={ROLE_SCHEME[normRole(selectedUser.role)] || 'gray'} borderRadius="full">{normRole(selectedUser.role)}</Badge>
                                            </HStack>
                                            <Text fontSize="xs" color={mutedColor} mt={0.5}>{selectedUser.email}</Text>
                                        </Box>
                                        {isDirty && (
                                            <MotionBox initial={{ scale:0 }} animate={{ scale:1 }} transition={ANIM.spring}>
                                                <Badge colorScheme="orange" fontSize="xs" borderRadius="full" px={3} py={1}
                                                    boxShadow="0 0 8px var(--chakra-colors-orange-200)">
                                                    Unsaved Changes
                                                </Badge>
                                            </MotionBox>
                                        )}
                                    </HStack>

                                    {/* Current Skills */}
                                    <Box>
                                        <HStack spacing={2} mb={3}>
                                            <Icon as={MdBuild} color="blue.400" boxSize={3.5} />
                                            <FormLabel mb={0} fontSize="sm" fontWeight="700">
                                                Current Skills ({currentSkills.length})
                                            </FormLabel>
                                        </HStack>
                                        {currentSkills.length > 0 ? (
                                            <Wrap spacing={2}>
                                                <AnimatePresence>
                                                    {currentSkills.map((skill, i) => (
                                                        <WrapItem key={skill}>
                                                            <SkillTag skill={skill} onRemove={handleRemoveSkill} index={i} />
                                                        </WrapItem>
                                                    ))}
                                                </AnimatePresence>
                                            </Wrap>
                                        ) : (
                                            <Text color={mutedColor} fontSize="sm" fontStyle="italic">
                                                No skills defined for this technician.
                                            </Text>
                                        )}
                                    </Box>

                                    <Divider />

                                    {/* Quick Add */}
                                    <Box>
                                        <HStack spacing={2} mb={3}>
                                            <Icon as={MdLightbulb} color="orange.400" boxSize={3.5} />
                                            <FormLabel mb={0} fontSize="sm" fontWeight="700">Quick Add Common Skills</FormLabel>
                                        </HStack>
                                        <Wrap spacing={2}>
                                            {COMMON_SKILLS.filter(s => !currentSkills.includes(s)).map(skill => (
                                                <WrapItem key={skill}>
                                                    <QuickSkillButton skill={skill} onAdd={handleAddSkill} />
                                                </WrapItem>
                                            ))}
                                            {COMMON_SKILLS.filter(s => !currentSkills.includes(s)).length === 0 && (
                                                <Text fontSize="xs" color={mutedColor}>All common skills assigned ✓</Text>
                                            )}
                                        </Wrap>
                                    </Box>

                                    <Divider />

                                    {/* --- UPDATE: copy from another person */}
                                    {copySources.length > 0 && (
                                        <Box>
                                            <FormLabel fontSize="sm" fontWeight="700">Copy skills from…</FormLabel>
                                            <Select size="sm" borderRadius="lg" placeholder="Choose a person to copy from" onChange={handleCopyFrom} defaultValue="">
                                                {copySources.map(u => <option key={u.id} value={u.id}>{u.username} · {parseSkills(u.skills).length} skills</option>)}
                                            </Select>
                                        </Box>
                                    )}

                                    {/* Custom Skill Input */}
                                    <Box>
                                        <FormLabel fontSize="sm" fontWeight="700">Add Custom Skill</FormLabel>
                                        <HStack>
                                            <Input placeholder="Enter skill name (e.g., leak_detection)"
                                                value={newSkillInput} onChange={e => setNewSkillInput(e.target.value)}
                                                onKeyPress={e => { if (e.key === 'Enter') handleAddSkill(newSkillInput); }}
                                                borderRadius="lg" size="sm"
                                                _focus={{ borderColor:'teal.400', boxShadow:'0 0 0 1px var(--chakra-colors-teal-400)' }} />
                                            <IconButton icon={<CheckIcon />} colorScheme="green" size="sm"
                                                onClick={() => handleAddSkill(newSkillInput)}
                                                isDisabled={!newSkillInput.trim()} aria-label="Add skill"
                                                borderRadius="lg" />
                                        </HStack>
                                        <Text fontSize="2xs" color={mutedColor} mt={1}>
                                            Use lowercase with underscores (e.g., pump_mechanics)
                                        </Text>
                                    </Box>

                                    <Divider />

                                    {/* Advanced Editor (collapsible) */}
                                    <Box>
                                        <Button size="xs" variant="ghost" onClick={() => setShowAdvanced(!showAdvanced)}
                                            leftIcon={<EditIcon />} color={mutedColor} mb={2}>
                                            {showAdvanced ? 'Hide' : 'Show'} Advanced Editor
                                        </Button>
                                        <Collapse in={showAdvanced} animateOpacity>
                                            <FormControl>
                                                <Textarea placeholder={'pump_mechanics\nelectrical\nvalve_repair…'}
                                                    value={skillsText} onChange={handleTextareaChange}
                                                    rows={8} fontFamily="mono" fontSize="xs" borderRadius="lg" />
                                                <Text fontSize="2xs" color={mutedColor} mt={1}>
                                                    {currentSkills.length} skills defined
                                                </Text>
                                            </FormControl>
                                        </Collapse>
                                    </Box>

                                    <Divider />

                                    {/* Action Buttons */}
                                    <HStack justify="space-between">
                                        <Button variant="ghost" colorScheme="red" size="sm"
                                            onClick={handleResetSkills} isDisabled={currentSkills.length === 0}
                                            borderRadius="lg" leftIcon={<DeleteIcon />}>
                                            Clear All
                                        </Button>
                                        <HStack spacing={2}>
                                            <Button variant="ghost" size="sm" borderRadius="lg"
                                                onClick={() => { setSkillsText(parseSkills(selectedUser.skills).join('\n')); setIsDirty(false); }}
                                                isDisabled={!isDirty}>
                                                Discard
                                            </Button>
                                            <MotionBox whileHover={{ scale:1.02 }} whileTap={{ scale:0.98 }}>
                                                <Button colorScheme="teal" size="sm" onClick={handleSave}
                                                    isLoading={isSaving} isDisabled={!selectedUserId || !isDirty}
                                                    leftIcon={<CheckIcon />} borderRadius="lg"
                                                    boxShadow={isDirty ? '0 4px 12px rgba(56,178,172,0.3)' : 'none'}
                                                    _hover={{ boxShadow:'0 6px 18px rgba(56,178,172,0.4)', transform:'translateY(-1px)' }}
                                                    transition="all 0.2s">
                                                    Save Skills
                                                </Button>
                                            </MotionBox>
                                        </HStack>
                                    </HStack>
                                </VStack>
                            </CardBody>
                        </Card>
                    </MotionBox>
                ) : (
                    <MotionBox key="empty" {...ANIM.scaleIn} transition={ANIM.smooth}>
                        <Card bg={bgColor} borderColor={borderColor} borderWidth="1px" borderRadius="xl">
                            <CardBody>
                                <EmptyState icon={MdEngineering}
                                    title="Select a technician to manage their skills"
                                    subtitle="Use the dropdown above to choose a technician" />
                            </CardBody>
                        </Card>
                    </MotionBox>
                )}
            </AnimatePresence>
        </Box>
    );
}

export default TechnicianSkillsManager;